import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import { motion } from 'framer-motion'

const LAST_UPDATED = 'February 2026'

export default function TermsPage() {
  return (
    <main data-testid="terms-page">
      <Navbar />
      <section className="pt-32 pb-24 px-6 md:px-12 lg:px-24 bg-white min-h-screen">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-6 font-body font-semibold">
              Legal
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter text-[#0A0A0A] mb-4 leading-tight">
              Terms of Use
            </h1>
            <p className="text-sm font-body text-[#0A0A0A]/40 mb-16">
              Last updated: {LAST_UPDATED}
            </p>

            <div className="space-y-12">
              <Section title="1. Acceptance of Terms">
                <p>
                  By accessing and using the Strive Partners website (strivepartners.co), you agree to be bound by these Terms of Use. If you do not agree, please do not use the site. These terms apply to all visitors, users, and others who access the site.
                </p>
              </Section>

              <Section title="2. Use of the Website">
                <p>You may use this website for lawful purposes only. You agree not to:</p>
                <ul>
                  <li>Use the site in any way that violates applicable laws or regulations</li>
                  <li>Attempt to gain unauthorised access to any part of the site or its systems</li>
                  <li>Transmit any unsolicited or unauthorised advertising or promotional material</li>
                  <li>Introduce any malicious code, viruses, or other harmful material</li>
                  <li>Scrape, crawl, or harvest data from the site without prior written consent</li>
                </ul>
              </Section>

              <Section title="3. No Guarantee of Outcomes">
                <p>
                  Content on this website, including case study descriptions, service outlines, and capability descriptions, is provided for general information purposes only. Strive Partners does not guarantee any specific placement outcomes, timelines, or search results. Past engagements referenced on the site are illustrative and anonymised; they do not constitute a representation that similar outcomes will be achieved for any future client.
                </p>
              </Section>

              <Section title="4. Intellectual Property">
                <p>
                  All content on this website — including text, design, graphics, logos, and the Strive Partners brand — is the intellectual property of Strive Partners and is protected by applicable copyright and trademark laws. You may not reproduce, distribute, or create derivative works from any content on this site without our prior written permission.
                </p>
              </Section>

              <Section title="5. Third-Party Links">
                <p>
                  This website may contain links to third-party websites (including Strive Hunt at strivehunt.com and LinkedIn). These links are provided for your convenience only. Strive Partners has no control over the content of those sites and accepts no responsibility for them or for any loss or damage that may arise from your use of them.
                </p>
              </Section>

              <Section title="6. Limitation of Liability">
                <p>
                  To the fullest extent permitted by law, Strive Partners excludes all liability for any loss or damage — whether direct, indirect, incidental, consequential, or punitive — arising from your use of this website or reliance on its content. This includes but is not limited to loss of revenue, data, goodwill, or business opportunity.
                </p>
              </Section>

              <Section title="7. Disclaimer of Warranties">
                <p>
                  This website is provided on an "as is" and "as available" basis without any warranty of any kind, whether express or implied. We do not warrant that the site will be uninterrupted, error-free, or free from viruses or other harmful components.
                </p>
              </Section>

              <Section title="8. Contact Form Submissions">
                <p>
                  By submitting an enquiry through our contact form, you consent to Strive Partners using that information to respond to your enquiry. Submission does not create any contractual obligation on either party. Any engagement with Strive Partners as a retained search provider is subject to a separate written agreement.
                </p>
              </Section>

              <Section title="9. Changes to These Terms">
                <p>
                  We reserve the right to modify these Terms at any time. Changes are effective immediately upon posting to the site. Your continued use of the site following changes constitutes acceptance of the revised Terms.
                </p>
              </Section>

              <Section title="10. Governing Law">
                <p>
                  These Terms are governed by and construed in accordance with the laws of India. Any disputes arising from or relating to these Terms or your use of the site shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.
                </p>
              </Section>

              <Section title="11. Contact Us">
                <p>
                  For questions about these Terms, please contact us at{' '}
                  <a href="mailto:sahil@strivepartners.co" className="text-[#0B1F3A] underline underline-offset-2">
                    sahil@strivepartners.co
                  </a>.
                </p>
              </Section>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </main>
  )
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-heading font-bold text-[#0A0A0A] mb-4 tracking-tight">{title}</h2>
      <div className="text-sm font-body text-[#0A0A0A]/60 leading-relaxed space-y-3 [&_ul]:list-none [&_ul]:space-y-2 [&_ul_li]:flex [&_ul_li]:items-start [&_ul_li]:gap-2 [&_ul_li]:before:content-['—'] [&_ul_li]:before:text-[#0A0A0A]/25 [&_ul_li]:before:flex-shrink-0">
        {children}
      </div>
    </div>
  )
}
